
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { ObjectFeaturesEnum } from "../../common/enums/ObjectFeaturesEnum";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { IKeyValuePair } from "../../common/models/KeyValuePair";
import { RouteFromObjectType } from "../../common/utilities/RouteFromObjectType";
import { routes, UIViewCategory } from "../../routes/routes";
import { IOfflineDevice, IOfflineTankBattery } from "../offline-data-service/Models";
import OfflineDataService from "../offline-data-service/OfflineDataService";
import restService from "../rest-service/RestService";
import { INavigationItem } from "./Models";

export type NavigationItemOptions = {
    includeChildren?: boolean;
    includeAncestors?: boolean;
};

// query params for the actual data call
type _RelatedObjectDataQueryParams = {
    viewID: UIViewEnum;
    wellID?: number;
    deviceID?: number;
    tankID?: number;
    tagID?: number;
    tankBatteryID?: number;
};

export type RelatedObjectOptions = {
    tankBatteryID?: number;
    wellID?: number;
    deviceID?: number;
    tankID?: number;
    tagID?: number;
};

type _AvailableViewsQueryParams = {
    contextGroupID: number;
};

async function _loadNavigationGroups(signal?: AbortSignal): Promise<INavigationItem[]> {
    const response = await restService.get<INavigationItem[]>(`/navigation/groups`, signal);
    return response.data;
}

const NavigationDataService = {

    loadNavigationGroups: async(signal?: AbortSignal): Promise<INavigationItem[]> => {
        return _loadNavigationGroups(signal);
    },

    getNavigationGroup: async(customerID: number,
        objectID: number,
        options: NavigationItemOptions): Promise<INavigationItem | undefined> => {

        let group: INavigationItem | undefined;

        const promises: Array<Promise<INavigationItem | undefined | INavigationItem[]>> = [
            OfflineDataService.getGroup(customerID, objectID)
        ];

        if (options.includeChildren) {
            promises.push(OfflineDataService.getChildGroups(customerID, objectID));
        }

        if (options.includeAncestors) {
            promises.push(OfflineDataService.getGroupAncestory(customerID, objectID));
        }

        await Promise.all(promises);

        let index = 0;
        group = await promises[index++] as INavigationItem | undefined;

        if (group && options.includeChildren) {
            const children = await promises[index++];
            if (children) {
                group.children = children as INavigationItem[];
            }
        }

        if (group && options.includeAncestors) {
            const ancestors = await promises[index++];
            if (ancestors) {
                group.ancestors = ancestors as INavigationItem[];

                group.fullPath = group.ancestors.map(o => o.title).join('/') + `/${group.title}`;
            }

        }
   
        return group;
    },
    getNavigationGroups: async (customerID: number, offline: boolean, signal?: AbortSignal): Promise<INavigationItem[]> => {
        
        let groups = await OfflineDataService.getGroups(customerID);

        if (offline) {
            return groups;
        }

        if (!groups.length) {
            groups = await _loadNavigationGroups(signal);
        }

        return groups;
    },
    refreshDynamicGroup: async(groupID: number, signal?: AbortSignal): Promise<void> => {
        await restService.get<void>(`/groups/${groupID}/navigation/refresh/dynamic`, signal);
    },
    getRelatedObjects: async (
        customerID: number, 
        contextGroupID: number,  
        eLynxObject: IeLynxObject, 
        viewID: UIViewEnum,
        options: RelatedObjectOptions,
        offline: boolean,         
        signal?: AbortSignal): Promise<INavigationItem[]> => {
        let data: INavigationItem[] = [];

        if (!offline) {

            let url: string | undefined;
            switch(eLynxObject.objectType) {

                case eLynxObjectTypeEnum.Device:
                    url = `/groups/${contextGroupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/navigation/availableDevices`;
                    break;

                case eLynxObjectTypeEnum.Tank:
                    url = options.deviceID
                        ? `/groups/${contextGroupID}/devices/${options.deviceID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/navigation/availableTanks`
                        : options.tankBatteryID
                            ? `/groups/${contextGroupID}/tankBatteries/${options.tankBatteryID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/navigation/availableTanks`
                            : `/groups/${contextGroupID}/tanks/${eLynxObject.id}/navigation/availableTanks`;
                    break;

                case eLynxObjectTypeEnum.TankBattery:

                    url = `/groups/${contextGroupID}/tankbatteries/navigation`;
                    break;
                        
                case eLynxObjectTypeEnum.Well:
                    url = `/groups/${contextGroupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/navigation/availableWells`;
                    break;

                case eLynxObjectTypeEnum.Tag:

                    // special case for tags
                    const p1 = restService.get<INavigationItem[], _RelatedObjectDataQueryParams>(`/groups/${contextGroupID}/navigation/tagParentObjectDetails`, signal, {
                        viewID: viewID,
                        wellID: options.wellID,
                        deviceID: options.deviceID,
                        tankID: options.tankID,
                        tagID: eLynxObject.id,
                        tankBatteryID: options.tankBatteryID
                    });
       
                    const p2 = restService.get<INavigationItem[], _RelatedObjectDataQueryParams>(`/groups/${contextGroupID}/navigation/availableTags`, signal, {
                        viewID: viewID,
                        tagID: eLynxObject.id
                    });

                    const [parents, tags] = await Promise.all([p1, p2]);

                    data = parents.data.concat(tags.data);
                    return data;

                default:

                    break;
            }

            if (url) {
                const response = await restService.get<INavigationItem[], _RelatedObjectDataQueryParams>(url, signal, {
                    viewID: viewID,
                    wellID: options.wellID,
                    deviceID: options.deviceID,
                    tankID: options.tankID,
                });


                

                data = response.data;
            }
        } else {

            switch(eLynxObject.objectType) {

                case eLynxObjectTypeEnum.Device:

                    const groupDevices = await OfflineDataService.getGroupDevices(customerID, 
                        contextGroupID,
                        false,
                        ObjectFeaturesEnum.ManualEntry
                    );

                    const devices = groupDevices.map((value) => {
                        const item: INavigationItem = {
                            objectID: value.id,
                            objectTypeID: eLynxObjectTypeEnum.Device,
                            title: value.name,
                            order: 0,
                            allowClick: true,
                            hasChildren: false,
                            isDynamic: false,
                            isHidden: false,
                        };
                        return item;
                    });

                    data = data.concat(devices);

                    break;

                case eLynxObjectTypeEnum.Tank:
                    const tanks = await OfflineDataService.getTanks(customerID, eLynxObject);

                    if (options.tankBatteryID) {
                        let parent = await  OfflineDataService.getObject(customerID, {
                            id: options.tankBatteryID,
                            objectType: eLynxObjectTypeEnum.TankBattery
                        }, ObjectFeaturesEnum.ManualEntry);

                        data.push({
                            objectID: parent?.id as number,
                            objectTypeID: eLynxObjectTypeEnum.TankBattery,
                            title: parent?.name as string,
                            order: 0,
                            allowClick: true,
                            hasChildren: false,
                            isDynamic: false,
                            isHidden: false,
                        });

                    } else {
                        let parent = await  OfflineDataService.getObject(customerID, {
                            id: options.deviceID as number,
                            objectType: eLynxObjectTypeEnum.Device
                        }, ObjectFeaturesEnum.ManualEntry);

                        data.push({
                            objectID: parent?.id as number,
                            objectTypeID: eLynxObjectTypeEnum.Device,
                            title: parent?.name as string,
                            order: 0,
                            allowClick: true,
                            hasChildren: false,
                            isDynamic: false,
                            isHidden: false,
                        });
                    }

                    data = data.concat(tanks.map((value) => {
                        const item: INavigationItem = {
                            objectID: value.id,
                            objectTypeID: eLynxObjectTypeEnum.Tank,
                            title: value.name,
                            order: 0,
                            allowClick: true,
                            hasChildren: false,
                            isDynamic: false,
                            isHidden: false,
                        };
                        return item;
                    }));

                    break;
                
                case eLynxObjectTypeEnum.TankBattery:
                    const tankBatteries = await OfflineDataService.getGroupTankBatteries(customerID, contextGroupID, ObjectFeaturesEnum.ManualEntry);

                    data = tankBatteries.map((value) => {
                        const item: INavigationItem = {
                            objectID: value.id,
                            objectTypeID: eLynxObjectTypeEnum.TankBattery,
                            title: value.name,
                            order: 0,
                            allowClick: true,
                            hasChildren: false,
                            isDynamic: false,
                            isHidden: false,
                        };
                        return item;
                    });
                    break;


                case eLynxObjectTypeEnum.Well:
                    const wells = await OfflineDataService.getGroupWells(customerID, contextGroupID, ObjectFeaturesEnum.ManualEntry);

                    data = wells.map((value) => {
                        const item: INavigationItem = {
                            objectID: value.id,
                            objectTypeID: eLynxObjectTypeEnum.Well,
                            title: value.name,
                            order: 0,
                            allowClick: true,
                            hasChildren: false,
                            isDynamic: false,
                            isHidden: false,
                        };
                        return item;
                    });
                    break;


                // tag views are available offline
                //case eLynxObjectTypeEnum.Tag:
                //    break;

                default:
                    break;
            }
        }

        return data;
    },
    getAvailableViewsForGroups: async (customerID: number, 
        contextGroupID: number,
        groupIDs: number[],
        offline: boolean, 
        signal?: AbortSignal): Promise<IKeyValuePair<number, UIViewEnum[]>[]> => {

        let data: IKeyValuePair<number, UIViewEnum[]>[] = [];

        if (!offline) {
            const response = await restService.post<number[], 
                                        IKeyValuePair<number, UIViewEnum[]>[],
                                        _AvailableViewsQueryParams>("groups/navigation/availableViews",
                                    groupIDs, 
                                    signal,
                                    undefined, {
                                    contextGroupID: contextGroupID
                                });
            data = response.data;
        } else {
            if (customerID) {
                data = groupIDs.map((o) => {
                    const item: IKeyValuePair<number, UIViewEnum[]> = {
                        Key: o,
                        Value: [UIViewEnum.Group_SummaryListing] 
                    };

                    return item;
                });
            }
        }

        return data;
    },
    getAvailableViews: async (customerID: number, 
        contextGroupID: number, 
        eLynxObject: IeLynxObject,
        offline: boolean,
        signal?: AbortSignal): Promise<UIViewEnum[]> => {

        let data: UIViewEnum[] = [];

        if (!offline) {

            let url: string | undefined;
            switch(eLynxObject.objectType) {

                case eLynxObjectTypeEnum.Group:
                    url = `/groups/${contextGroupID}/navigation/availableViews`;                
                    break;

                case eLynxObjectTypeEnum.Device:
                case eLynxObjectTypeEnum.Well:
                case eLynxObjectTypeEnum.Tank:
                case eLynxObjectTypeEnum.TankBattery:
                case eLynxObjectTypeEnum.Tag:                    
                    url = `/groups/${contextGroupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/navigation/availableViews`;
                    break;

    
                default:
                    throw new Error(`getAvailableViews not implemented for ${eLynxObject.objectType}`);
            }

            if (url) {
                const response = await restService.get<UIViewEnum[]>(url, signal);
                data = response.data;

                // if (eLynxObject.objectType === eLynxObjectTypeEnum.Device) {
                //     data.push(UIViewEnum.Device_CustomForm);
                // }
            }
        } else {
            switch(eLynxObject.objectType) {

                case eLynxObjectTypeEnum.Group:
                    const groupDevices = OfflineDataService.getGroupDevices(customerID,
                        contextGroupID,
                        false,
                        ObjectFeaturesEnum.ManualEntry
                    );

                    const groupTankBatteries = OfflineDataService.getGroupTankBatteries(customerID,
                        contextGroupID,
                        ObjectFeaturesEnum.ManualEntry
                    );

                    const groupWells = OfflineDataService.getGroupWells(customerID,
                        contextGroupID,
                        ObjectFeaturesEnum.ManualEntry
                    );

                    const [devices, tankBatteries, wells] = await Promise.all([
                        groupDevices,
                        groupTankBatteries,
                        groupWells
                    ]);

                    data = routes.filter(o => o.allowOffline && o.category === UIViewCategory.Group &&
                        (o.viewID !== UIViewEnum.Group_SummaryListing_Devices || (devices.length > 0)) &&
                        (o.viewID !== UIViewEnum.Group_SummaryListing_Wells || (wells.length > 0)) &&
                        (o.viewID !== UIViewEnum.Group_SummaryListing_TankBatteries || (tankBatteries.length > 0)))
                                .map(o => o.viewID as UIViewEnum);
                    break;

                case eLynxObjectTypeEnum.Device:
                    //Run tickets are only available if device has a tank battery with tanks
                    const device = (await OfflineDataService.getObject(customerID, eLynxObject, undefined, {includeTanks: true})) as IOfflineDevice;
                    
                    data = routes.filter(o => o.allowOffline && o.category === UIViewCategory.Device &&
                        (o.viewID !== UIViewEnum.Device_Details_RunTickets || ((device?.tanks?.length ?? 0) > 0)))
                                .map(o => o.viewID as UIViewEnum);
                    break;

                case eLynxObjectTypeEnum.Well:
                    data = routes.filter(o => o.allowOffline && o.category === UIViewCategory.Well)
                                .map(o => o.viewID as UIViewEnum);
                    break;

                case eLynxObjectTypeEnum.Tank:
                    data = routes.filter(o => o.allowOffline && o.category === UIViewCategory.Tank)
                                .map(o => o.viewID as UIViewEnum);
                    break;

                case eLynxObjectTypeEnum.TankBattery:
                    //Run tickets are only available if the tank battery has tanks      
                    const tankBattery = (await OfflineDataService.getObject(customerID, eLynxObject, undefined, { includeTanks: true })) as IOfflineTankBattery;

                    data = routes.filter(o => o.allowOffline && o.category === UIViewCategory.TankBattery &&
                        (o.viewID !== UIViewEnum.TankBattery_Details_RunTickets || ((tankBattery?.tanks?.length ?? 0) > 0)))
                        .map(o => o.viewID as UIViewEnum);
                    break;

                case eLynxObjectTypeEnum.Tag:                    
                    data = routes.filter(o => o.allowOffline && o.category === UIViewCategory.Tag)
                                .map(o => o.viewID as UIViewEnum);
                    break;

   
                default:
                    throw new Error(`getAvailableViews not implemented for ${eLynxObject.objectType}`);
            }

        }

        return data;
    },
    getAvailableAdminViews: async (contextGroupID: number,
        signal?: AbortSignal): Promise<UIViewEnum[]> => {

        const response = await restService.get<UIViewEnum[]>(`groups/${contextGroupID}/navigation/availableAdminViews`, signal);
        return response.data;
    },
  
};

export default NavigationDataService;

